import React, { useState } from 'react';
import { CheckCircle, Star } from 'lucide-react';

// ─── Deterministic question-type assignment ───────────────────────────────────
const QUESTION_TYPES = ['mcq', 'rating', 'truefalse', 'mcq', 'mcq', 'rating', 'truefalse'];

export function getQuestionType(indicatorId = '') {
    let hash = 0;
    for (let i = 0; i < indicatorId.length; i++) {
        hash = (hash * 31 + indicatorId.charCodeAt(i)) & 0xffffffff;
    }
    return QUESTION_TYPES[Math.abs(hash) % QUESTION_TYPES.length];
}

// ─── Friendly option labels (no level numbers shown) ─────────────────────────
const MCQ_LABEL_SETS = [
    ['Just getting started', 'Making some progress', 'Doing this consistently', 'Leading the way'],
    ['Not yet in place', 'Partially in place', 'Mostly in place', 'Fully embedded'],
    ['Rarely happens', 'Sometimes happens', 'Usually happens', 'Always happens'],
    ['Not established', 'Beginning to develop', 'Well established', 'Exemplary practice'],
];

function getLabelSet(indicatorId = '') {
    let hash = 0;
    for (let i = 0; i < indicatorId.length; i++) {
        hash = (hash * 7 + indicatorId.charCodeAt(i)) & 0xffffffff;
    }
    return MCQ_LABEL_SETS[Math.abs(hash) % MCQ_LABEL_SETS.length];
}

// ─── MCQ ─────────────────────────────────────────────────────────────────────
function MCQQuestion({ indicator, selectedLevel, onSelect }) {
    const labels = getLabelSet(indicator.indicator_id);
    const rubric = indicator.rubric || {};

    const options = [1, 2, 3, 4].map(level => ({
        level,
        label: labels[level - 1],
        descriptor: rubric[`level_${level}`] || '',
    }));

    // Brand-aligned option colors: red → amber → blue → brand-green
    const palette = [
        { selBg: 'bg-red-50',    selBorder: 'border-red-400',    dot: 'bg-red-400',    label: 'text-red-700'    },
        { selBg: 'bg-amber-50',  selBorder: 'border-amber-400',  dot: 'bg-amber-400',  label: 'text-amber-700'  },
        { selBg: 'bg-blue-50',   selBorder: 'border-blue-400',   dot: 'bg-blue-400',   label: 'text-blue-700'   },
        { selBg: 'bg-green-50',  selBorder: 'border-green-500',  dot: 'bg-green-500',  label: 'text-green-700'  },
    ];

    return (
        <div className="space-y-2.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                Select the option that best describes your school
            </p>
            {options.map((opt, idx) => {
                const p = palette[idx];
                const isSelected = selectedLevel === opt.level;
                return (
                    <button
                        key={opt.level}
                        onClick={() => onSelect(opt.level)}
                        className={`
                            w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 cursor-pointer group
                            ${isSelected
                                ? `${p.selBg} ${p.selBorder} shadow-sm`
                                : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'
                            }
                        `}
                    >
                        <div className="flex items-center gap-3">
                            {/* Radio dot */}
                            <div className={`
                                w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all
                                ${isSelected ? `${p.dot} border-transparent` : 'border-gray-300 group-hover:border-gray-400'}
                            `}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm leading-snug ${isSelected ? p.label : 'text-gray-700'}`}>
                                    {opt.label}
                                </p>
                                {opt.descriptor && (
                                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed line-clamp-2">
                                        {opt.descriptor}
                                    </p>
                                )}
                            </div>
                            {isSelected && <CheckCircle className={`w-4 h-4 flex-shrink-0 ${p.label}`} />}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// ─── Rating Scale ─────────────────────────────────────────────────────────────
const RATING_LABELS = ['Beginning', 'Developing', 'Established', 'Exemplary'];
const RATING_TEXT   = ['text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-600'];
const RATING_DESC_BG = [
    'bg-red-50 border-red-100 text-red-700',
    'bg-amber-50 border-amber-100 text-amber-700',
    'bg-blue-50 border-blue-100 text-blue-700',
    'bg-green-50 border-green-100 text-green-700',
];

function RatingQuestion({ indicator, selectedLevel, onSelect }) {
    const rubric = indicator.rubric || {};
    const [hovered, setHovered] = useState(null);
    const active = hovered ?? selectedLevel;

    return (
        <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
                Rate your school's current practice
            </p>

            {/* 4-step track */}
            <div className="relative flex items-end justify-between mb-6 px-2">
                {/* Connecting line */}
                <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-100" />
                <div
                    className="absolute top-5 left-8 h-0.5 bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] transition-all duration-300"
                    style={{ width: active ? `${((active - 1) / 3) * 100}%` : '0%' }}
                />

                {[1, 2, 3, 4].map(level => {
                    const isActive = active >= level;
                    const isSelected = selectedLevel === level;
                    return (
                        <button
                            key={level}
                            onClick={() => onSelect(level)}
                            onMouseEnter={() => setHovered(level)}
                            onMouseLeave={() => setHovered(null)}
                            className="relative flex flex-col items-center gap-2 cursor-pointer z-10"
                        >
                            <div className={`
                                w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200
                                ${isActive
                                    ? 'bg-[#D67C2C] border-[#D67C2C] shadow-md shadow-[#D67C2C]/30'
                                    : 'bg-white border-gray-200 hover:border-[#D67C2C]/50'
                                }
                                ${isSelected ? 'scale-110' : ''}
                            `}>
                                <Star className={`w-4 h-4 transition-colors ${isActive ? 'text-white fill-white' : 'text-gray-300'}`} />
                            </div>
                            <span className={`text-xs font-semibold transition-colors whitespace-nowrap ${isActive ? RATING_TEXT[level - 1] : 'text-gray-400'}`}>
                                {RATING_LABELS[level - 1]}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Active descriptor */}
            {active && rubric[`level_${active}`] && (
                <div className={`p-4 rounded-xl border text-sm leading-relaxed transition-all duration-300 ${RATING_DESC_BG[active - 1]}`}>
                    {rubric[`level_${active}`]}
                </div>
            )}
        </div>
    );
}

// ─── Quick Check (True/False style) ──────────────────────────────────────────
function TrueFalseQuestion({ indicator, selectedLevel, onSelect }) {
    const options = [
        { label: 'Not yet',   sublabel: "We haven't started this",          level: 1, color: 'border-red-200   hover:border-red-300',   selColor: 'bg-red-50   border-red-400',   textColor: 'text-red-600',   icon: '○' },
        { label: 'Partially', sublabel: "We've made some progress",          level: 2, color: 'border-amber-200 hover:border-amber-300', selColor: 'bg-amber-50 border-amber-400', textColor: 'text-amber-600', icon: '◑' },
        { label: 'Mostly',    sublabel: 'We do this most of the time',       level: 3, color: 'border-blue-200  hover:border-blue-300',  selColor: 'bg-blue-50  border-blue-400',  textColor: 'text-blue-600',  icon: '◕' },
        { label: 'Yes, fully',sublabel: 'This is embedded in our practice',  level: 4, color: 'border-green-200 hover:border-green-300', selColor: 'bg-green-50 border-green-500', textColor: 'text-green-700', icon: '●' },
    ];

    return (
        <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">
                How well does this describe your school?
            </p>
            <div className="grid grid-cols-2 gap-3">
                {options.map(opt => {
                    const isSelected = selectedLevel === opt.level;
                    return (
                        <button
                            key={opt.level}
                            onClick={() => onSelect(opt.level)}
                            className={`
                                p-4 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer
                                ${isSelected ? `${opt.selColor} shadow-sm` : `bg-white ${opt.color}`}
                            `}
                        >
                            <div className={`text-xl mb-2 font-bold transition-colors ${isSelected ? opt.textColor : 'text-gray-300'}`}>
                                {opt.icon}
                            </div>
                            <p className={`font-semibold text-sm ${isSelected ? opt.textColor : 'text-gray-700'}`}>
                                {opt.label}
                            </p>
                            <p className="text-xs text-gray-400 mt-1 leading-snug">{opt.sublabel}</p>
                            {isSelected && (
                                <div className="mt-2 flex justify-center">
                                    <CheckCircle className={`w-4 h-4 ${opt.textColor}`} />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main QuestionRenderer ────────────────────────────────────────────────────
const QuestionRenderer = ({ indicator, selectedLevel, onSelect }) => {
    const type = getQuestionType(indicator?.indicator_id || '');
    if (!indicator) return null;

    switch (type) {
        case 'rating':    return <RatingQuestion   indicator={indicator} selectedLevel={selectedLevel} onSelect={onSelect} />;
        case 'truefalse': return <TrueFalseQuestion indicator={indicator} selectedLevel={selectedLevel} onSelect={onSelect} />;
        case 'mcq':
        default:          return <MCQQuestion       indicator={indicator} selectedLevel={selectedLevel} onSelect={onSelect} />;
    }
};

export default QuestionRenderer;
